import { useTranslation } from "react-i18next";
import type { PlatformTypeName } from "@multi-chat/shared";
import { AppNotice } from "../common/AppNotice.js";
import { Badge } from "../ui/badge/index.js";
import { channelStatusLabelKey } from "../../features/channels/channel-connection-policy.js";
import { cn } from "../../lib/utils.js";
import { ChannelMark } from "./ChannelMark.js";

/** Start-aligned column shared by the connected channel pages. */
export function ConnectedChannelPage({
  testId,
  children,
  className,
}: {
  testId?: string;
  children: React.ReactNode;
  className?: string;
}): React.JSX.Element {
  return (
    <div
      className={cn(
        "h-full min-h-0 overflow-y-auto px-4 py-5 text-start [scrollbar-gutter:stable] md:px-6 md:py-6",
        className,
      )}
      data-testid={testId}
    >
      <div className="mx-auto grid w-full max-w-[40rem] gap-4">{children}</div>
    </div>
  );
}

const connectedChannelHeaderClass = "flex min-w-0 items-center gap-3";
const connectedChannelHeaderCopyClass = "grid min-w-0 gap-1";
const connectedChannelHeaderTitleRowClass = "flex min-w-0 flex-wrap items-center gap-2";
const connectedChannelHeaderTitleClass =
  "m-0 min-w-0 text-[1.25rem] font-semibold tracking-[-0.01em] text-foreground";
const connectedChannelHeaderIdentityClass = "min-w-0 text-[0.8125rem] text-muted";
const channelIdentitySkeletonBlockClass = "animate-pulse bg-subtle/70 motion-reduce:animate-none";

/** Loading stand-in for ConnectedChannelHeader. Same wrappers, visible blocks. */
function ChannelIdentitySkeleton(): React.JSX.Element {
  return (
    <header className={connectedChannelHeaderClass} data-testid="channel-identity-skeleton">
      <span
        className={cn(
          "inline-grid size-12 shrink-0 place-items-center rounded-xl",
          channelIdentitySkeletonBlockClass,
        )}
        aria-hidden="true"
      />
      <div className={connectedChannelHeaderCopyClass}>
        <div className={connectedChannelHeaderTitleRowClass}>
          <h1 className={connectedChannelHeaderTitleClass}>
            <span
              className={cn(
                "block h-[1.5em] w-40 max-w-full rounded",
                channelIdentitySkeletonBlockClass,
              )}
            />
          </h1>
          <Badge
            variant="success"
            className={cn("border-transparent text-transparent", channelIdentitySkeletonBlockClass)}
            aria-hidden="true"
          >
            <span className="inline-block h-[1.25em] w-12" />
          </Badge>
        </div>
        <div className={connectedChannelHeaderIdentityClass}>
          <span
            className={cn(
              "block h-[1.5em] w-36 max-w-full rounded",
              channelIdentitySkeletonBlockClass,
            )}
          />
        </div>
      </div>
    </header>
  );
}

/** Reserves the connected detail column while a channel GET is still in flight. */
export function ChannelDetailSkeleton({
  label,
  testId,
  showTabs = false,
}: {
  label: string;
  testId?: string;
  showTabs?: boolean;
}): React.JSX.Element {
  return (
    <ConnectedChannelPage testId={testId}>
      <div role="status" aria-live="polite" aria-busy="true" className="grid gap-4">
        <span className="sr-only">{label}</span>
        <div>
          <div className={showTabs ? "pb-4" : undefined}>
            <ChannelIdentitySkeleton />
          </div>
          {showTabs ? (
            <div className="flex h-10 items-stretch gap-1 border-b border-line" aria-hidden="true">
              <span className="inline-flex h-10 items-center px-3">
                <span className={cn("block h-4 w-24 rounded", channelIdentitySkeletonBlockClass)} />
              </span>
              <span className="inline-flex h-10 items-center px-3">
                <span className={cn("block h-4 w-32 rounded", channelIdentitySkeletonBlockClass)} />
              </span>
            </div>
          ) : null}
        </div>
        <div className="grid gap-3 rounded-xl border border-line bg-surface p-5">
          <div className="h-4 w-32 animate-pulse rounded bg-surface-muted motion-reduce:animate-none" />
          <div className="h-10 w-full animate-pulse rounded-md bg-surface-muted motion-reduce:animate-none" />
          <div className="ms-auto h-8 w-24 animate-pulse rounded-md bg-surface-muted motion-reduce:animate-none" />
        </div>
        <div className="grid gap-3 rounded-xl border border-line bg-surface p-5">
          <div className="h-4 w-40 animate-pulse rounded bg-surface-muted motion-reduce:animate-none" />
          <div className="h-3 w-full max-w-xs animate-pulse rounded bg-surface-muted motion-reduce:animate-none" />
          <div className="h-3 w-2/3 animate-pulse rounded bg-surface-muted motion-reduce:animate-none" />
        </div>
      </div>
    </ConnectedChannelPage>
  );
}

function channelDetailStatusVariant(state: string): "default" | "success" | "warning" | "error" {
  if (state === "CONNECTED") return "success";
  if (state === "ERROR") return "error";
  if (state === "PENDING_PHONE_SELECTION" || state === "PENDING_PAGE_SELECTION") return "warning";
  return "default";
}

/** Centered disconnected/recovery column shared by the channel detail routes. */
export function ChannelDetailFrame({
  testId,
  platformType,
  title,
  state,
  error,
  children,
}: {
  testId: string;
  platformType: PlatformTypeName;
  title: string;
  state: string;
  error: string | null;
  children: React.ReactNode;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="h-full min-h-0 overflow-y-auto px-4 pt-16 pb-10 md:px-8 md:pt-24" data-testid={testId}>
      <div className="mx-auto grid w-full max-w-[40rem] justify-items-center gap-4 text-center">
        <ChannelMark platformType={platformType} size="hero" />
        <div className="grid justify-items-center gap-1">
          <h1 className="m-0 text-[1.25rem] font-semibold text-foreground">{title}</h1>
          <Badge variant={channelDetailStatusVariant(state)}>{t(channelStatusLabelKey(state))}</Badge>
        </div>
        {error ? <AppNotice severity="error" description={error} /> : null}
        {children}
      </div>
    </div>
  );
}

export function ConnectedChannelHeader({
  platformType,
  title,
  badge,
  identity,
}: {
  platformType: PlatformTypeName;
  title: React.ReactNode;
  badge: React.ReactNode;
  identity?: React.ReactNode;
}): React.JSX.Element {
  return (
    <header className={connectedChannelHeaderClass}>
      <ChannelMark platformType={platformType} size="list" />
      <div className={connectedChannelHeaderCopyClass}>
        <div className={connectedChannelHeaderTitleRowClass}>
          <h1 className={connectedChannelHeaderTitleClass}>{title}</h1>
          {badge}
        </div>
        {identity ? <div className={connectedChannelHeaderIdentityClass}>{identity}</div> : null}
      </div>
    </header>
  );
}

export function ConnectedChannelCard({
  title,
  children,
  testId,
}: {
  title: string;
  children: React.ReactNode;
  testId?: string;
}): React.JSX.Element {
  return (
    <section
      className="overflow-hidden rounded-xl border border-line bg-surface"
      data-testid={testId}
    >
      <h2 className="m-0 border-b border-line px-4 py-3 text-[0.8125rem] font-semibold text-foreground md:px-5">
        {title}
      </h2>
      <div className="grid gap-4 px-4 py-4 md:px-5">{children}</div>
    </section>
  );
}

export function ConnectedChannelDetailList({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element {
  return <dl className="m-0 divide-y divide-line">{children}</dl>;
}

export function ConnectedChannelDetailRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="grid gap-1 py-3 first:pt-0 last:pb-0 sm:grid-cols-[minmax(9rem,13rem)_minmax(0,1fr)] sm:items-baseline sm:gap-4">
      <dt className="text-[0.75rem] font-medium text-muted">{label}</dt>
      <dd className="m-0 min-w-0 text-[0.8125rem] text-foreground">{children}</dd>
    </div>
  );
}

export function ConnectedChannelActions({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element {
  return <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line pt-3">{children}</div>;
}
